import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function Loans() {
  const [loans, setLoans] = useState([]);
  const [loanRequests, setLoanRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get loans
      const loansRes = await api.get('/api/loans');
      setLoans(loansRes.data);
      
      // Get loan requests (pending, pre-approved, AND rejected)
      const requestsRes = await api.get('/api/my-loan-requests');
      const myRequests = requestsRes.data.filter(r => 
        r.status === 'pending' || r.status === 'pre_approved' || r.status === 'rejected'
      );
      setLoanRequests(myRequests);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLoans = loans.filter(loan => {
    if (filter === 'all') return true;
    if (filter === 'pending_requests' || filter === 'rejected_requests') return false;
    return loan.status === filter;
  });
  
  const showRequests = filter === 'all' || filter === 'pending_requests' || filter === 'rejected_requests';
  const showLoans = filter === 'all' || (filter !== 'pending_requests' && filter !== 'rejected_requests' && filteredLoans.length > 0);
  
  // Filter requests by status
  const displayedRequests = filter === 'rejected_requests' 
    ? loanRequests.filter(r => r.status === 'rejected')
    : loanRequests.filter(r => r.status !== 'rejected');

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('loans.title')} />

        <main style={{ padding: '1rem' }} className="fade-in">
          {/* Filters */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            {[
              { value: 'all', label: `${t('status.all')} (${loans.length + loanRequests.length})`, gradient: 'linear-gradient(135deg, #103766 0%, #1a4d7a 100%)' },
              { value: 'pending_requests', label: `${t('loanRequest.pendingRequests')} (${loanRequests.filter(r => r.status !== 'rejected').length})`, gradient: 'linear-gradient(135deg, #FF9800 0%, #fb923c 100%)' },
              { value: 'rejected_requests', label: `${t('loanRequest.rejectedRequests')} (${loanRequests.filter(r => r.status === 'rejected').length})`, gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
              { value: 'active', label: `${t('status.active')} (${loans.filter(l => l.status === 'active').length})`, gradient: 'linear-gradient(135deg, #007a33 0%, #00a843 100%)' },
              { value: 'pending_signature', label: `${t('status.pending')} (${loans.filter(l => l.status === 'pending_signature').length})`, gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)' },
              { value: 'cancelled', label: `${t('status.cancelled')} (${loans.filter(l => l.status === 'cancelled').length})`, gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }
            ].map(({value, label, gradient}) => (
              <button
                key={value}
                onClick={() => setFilter(value)}
                data-testid={`filter-${value}`}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  border: filter === value ? 'none' : '2px solid #e5e7eb',
                  background: filter === value ? gradient : 'white',
                  color: filter === value ? 'white' : '#6b7280',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: filter === value ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Loans Grid */}
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Loan Requests (Pending or Rejected) */}
            {showRequests && displayedRequests.map((request) => (
              <div
                key={request._id}
                className="dark-card"
                style={{
                  padding: '1.25rem',
                  border: request.status === 'rejected' ? '2px solid rgba(239, 68, 68, 0.4)' : '2px solid rgba(255, 152, 0, 0.4)',
                }}
                data-testid={`loan-request-card-${request._id}`}
              >
                {/* Header: amount + status badge */}
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#FF9800', margin: 0 }}>
                    {request.requested_amount.toLocaleString('es-ES', { minimumFractionDigits: 0 })}€
                  </h3>
                  <span style={{
                    padding: '0.35rem 0.75rem',
                    background: request.status === 'rejected' ? 'rgba(239,68,68,0.2)' : 'rgba(255, 152, 0, 0.2)',
                    borderRadius: '20px', fontSize: '0.7rem', fontWeight: '600', whiteSpace: 'nowrap',
                    color: request.status === 'rejected' ? '#ef4444' : '#FF9800',
                    border: `1px solid ${request.status === 'rejected' ? 'rgba(239,68,68,0.4)' : 'rgba(255, 152, 0, 0.4)'}`
                  }}>
                    {request.status === 'rejected' ? t('waitlistAdmin.rejected') : t('loanRequest.pendingApproval')}
                  </span>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>
                    {t('loanRequest.suggestedRate') || "Tipus d'interès"}: <strong style={{ color: '#FF9800' }}>{request.suggested_interest_rate}%</strong> {t('loanRequest.annual') || 'anual'}
                  </p>
                </div>

                <div style={{ display: 'grid', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                      {t('loanRequest.requestDate') || 'DATA SOL·LICITUD'}
                    </p>
                    <p style={{ fontSize: '1.1rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                      {new Date(request.created_at).toLocaleDateString(t('common.locale') || 'ca-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  
                  {request.notes && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                        {t('loanRequest.notes') || 'NOTES'}
                      </p>
                      <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.8)', lineHeight: '1.6' }}>
                        {request.notes}
                      </p>
                    </div>
                  )}
                </div>

                <div style={{ 
                  padding: '1.25rem', 
                  background: request.status === 'rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)', 
                  borderRadius: '12px',
                  border: `1px solid ${request.status === 'rejected' ? 'rgba(239,68,68,0.3)' : 'rgba(59,130,246,0.3)'}`
                }}>
                  <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)', lineHeight: '1.6' }}>
                    {request.status === 'rejected' ? (
                      <>
                        <strong>✕ {t('loanRequest.statusTitle') || "Estat de la sol·licitud"}:</strong><br/>
                        La teva sol·licitud de {request.requested_amount.toLocaleString('es-ES')}€ ha estat <strong style={{ color: '#ef4444' }}>rebutjada</strong>.
                        {request.rejection_reason && (
                          <>
                            <br/><br/>
                            <strong>{t('cancellations.reason')}:</strong> {request.rejection_reason}
                          </>
                        )}
                        <br/><br/>
                        Si creus que és un error, escriu-nos a <a href="mailto:info@vivendius.cat" style={{ color: '#FF9800', textDecoration: 'underline' }}>info@vivendius.cat</a>
                      </>
                    ) : (
                      <>
                        <strong>ℹ️ {t('loanRequest.statusTitle') || "Estat de la sol·licitud"}:</strong><br/>
                        {t('loanRequest.statusMessage')?.replace('{amount}', request.requested_amount.toLocaleString('es-ES')) || 
                          `La teva sol·licitud de ${request.requested_amount.toLocaleString('es-ES')}€ està sent revisada per l'equip de Vivendius. Rebràs una notificació per email quan sigui aprovada i es generi el contracte per signar.`}
                      </>
                    )}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Active/Pending Loans */}
            {showLoans && filteredLoans.map((loan) => (
              <div
                key={loan._id}
                className="fintech-card"
                data-testid={`loan-card-${loan._id}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/loans/${loan._id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#103766', margin: 0 }}>
                        €{loan.capital.toLocaleString('es-ES')}
                      </h3>
                      <span className={`status-badge ${loan.status === 'active' ? 'active' : loan.status === 'cancelled' ? 'cancelled' : 'pending'}`} style={
                        loan.status === 'cancelled' ? { background: 'rgba(239,68,68,0.15)', color: '#ef4444' } :
                        loan.status === 'matured' ? { background: 'rgba(107,114,128,0.15)', color: '#9ca3af' } : {}
                      }>
                        {loan.status === 'active' ? '✓ ' + t('status.active') : 
                         loan.status === 'cancelled' ? t('status.cancelled') :
                         loan.status === 'matured' ? t('status.matured') : 
                         '⏳ ' + t('status.pending')}
                      </span>
                      {loan.cancellation_request && loan.cancellation_request.status === 'pending' && (
                        <span style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(249,115,22,0.15)',
                          color: '#fb923c',
                          borderRadius: '20px',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          border: '1px solid rgba(249,115,22,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span>⏳</span>
                          <span>{t('cancellations.inProgress')}</span>
                        </span>
                      )}
                      {loan.cancellation_request && loan.cancellation_request.status === 'approved' && (
                        <span style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(16,185,129,0.15)',
                          color: '#10b981',
                          borderRadius: '20px',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          border: '1px solid rgba(16,185,129,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span>✅</span>
                          <span>{t('cancellations.approvedLabel')}</span>
                        </span>
                      )}
                      {loan.cancellation_request && loan.cancellation_request.status === 'rejected' && (
                        <span style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(239,68,68,0.15)',
                          color: '#ef4444',
                          borderRadius: '20px',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          border: '1px solid rgba(239,68,68,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span>❌</span>
                          <span>{t('cancellations.rejectedLabel')}</span>
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '2rem', marginBottom: '1.5rem' }}>
                      <div>
                        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('loans.interestRate')}</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#103766' }}>{loan.interest_rate}% {t('loanDetail.annual')}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('loans.interestGenerated')}</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#007a33' }}>€{loan.interest_generated?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('loans.daysRemaining')}</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#f97316' }}>{loan.days_remaining || 0} {t('dashboard.days')}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('loanDetail.estimatedTotalReturn')}</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#7c3aed' }}>€{loan.total_return?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{t('loanDetail.loanTimeline')}</span>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#103766' }}>
                          {Math.max(0, Math.min(100, ((365 - (loan.days_remaining || 365)) / 365) * 100)).toFixed(0)}%
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '12px',
                        background: '#e5e7eb',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${Math.max(0, Math.min(100, ((365 - (loan.days_remaining || 365)) / 365) * 100))}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #007a33 0%, #66b3a1 100%)',
                          borderRadius: '10px',
                          transition: 'width 0.5s ease'
                        }}></div>
                      </div>
                    </div>
                    
                    {/* Contract Download Button */}
                    {loan.contract_id && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const response = await api.get(`/api/contracts/${loan.contract_id}/download`, {
                                responseType: 'blob'
                              });
                              
                              // Create blob URL and trigger download
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `contracte_${loan.contract_id}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              toast.error('Error en descarregar el contracte: ' + (error.response?.data?.detail || 'Error desconegut'));
                            }
                          }}
                          style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                            color: 'white',
                            border: 'none',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                          data-testid={`download-contract-${loan._id}`}
                        >
                          📥 Descarregar Contracte
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Empty State */}
            {!showRequests && filteredLoans.length === 0 && (
              <div style={{ textAlign: 'center', padding: '6rem 0' }}>
                <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>💼</div>
                <p style={{ fontSize: '1.5rem', color: '#6b7280', fontWeight: '500' }}>
                  No hi ha préstecs amb aquest filtre
                </p>
              </div>
            )}
            
            {showRequests && loanRequests.length === 0 && (
              <div style={{ textAlign: 'center', padding: '6rem 0' }}>
                <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>📭</div>
                <p style={{ fontSize: '1.5rem', color: '#6b7280', fontWeight: '500' }}>
                  {t('loanRequest.noPendingRequests')}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', marginTop: '1rem' }}>
                  {t('loanRequest.canRequestFromDashboard')}
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Loans;
